import type { Theme } from '../theme/contract';
import { cozyPastel } from '../theme/themes/cozy-pastel';
import { commandCenter } from '../theme/themes/command-center';
import { warmNeutralTeal } from '../theme/themes/warm-neutral-teal';

/**
 * The theme registry — the ONE place a theme is wired into the app. Adding a new theme is:
 *   1. create `theme/themes/<id>.ts` exporting a `Theme`, then
 *   2. add a {@link RegisteredTheme} entry here.
 * No other code changes: the CSS generator walks `THEMES`, the picker renders `themeRegistry`, and
 * the provider validates against it. (Design-system §"DoD for theming".)
 *
 * `labelKey` is an i18n key (never a literal string — ADR-0025); the picker translates it.
 * `swatches` are raw hex purely for the small preview dots in the picker UI — they are NOT consumed
 * by components (components use semantic tokens), so a hex here does not violate the token rule.
 */
export interface RegisteredTheme {
  readonly id: string;
  /** i18n key for the human label, e.g. `settings.theme.cozyPastel`. */
  readonly labelKey: string;
  /** Which persona this theme is the default for (drives persona->theme selection). */
  readonly persona: 'kid' | 'family' | 'admin';
  /** 3–4 representative hex colors for the picker's preview dots only. */
  readonly swatches: readonly string[];
  readonly theme: Theme;
}

export const themeRegistry: readonly RegisteredTheme[] = [
  {
    id: warmNeutralTeal.id,
    labelKey: 'settings:theme.warmNeutralTeal',
    persona: 'family',
    swatches: ['#2F8F77', '#A66A1E', '#B4554C', '#FAFAF8'],
    theme: warmNeutralTeal,
  },
  {
    id: cozyPastel.id,
    labelKey: 'settings:theme.cozyPastel',
    persona: 'kid',
    swatches: ['#7458B5', '#B8E0D2', '#FFD9C0', '#FAF7F5'],
    theme: cozyPastel,
  },
  {
    id: commandCenter.id,
    labelKey: 'settings:theme.commandCenter',
    persona: 'admin',
    swatches: ['#4F46E5', '#0EA5E9', '#15803D', '#F6F8FB'],
    theme: commandCenter,
  },
];

/** All registered `Theme` objects (used by the CSS generator). */
export const THEMES: readonly Theme[] = themeRegistry.map((entry) => entry.theme);

/** All valid theme ids as a union-friendly readonly tuple. */
export const THEME_IDS = themeRegistry.map((entry) => entry.id);

const REGISTRY_BY_ID = new Map(themeRegistry.map((entry) => [entry.id, entry]));

/** The default theme for a persona (kid -> cozy-pastel, family -> warm-neutral-teal, admin -> command-center). */
export function defaultThemeIdForPersona(persona: 'kid' | 'family' | 'admin'): string {
  const match = themeRegistry.find((entry) => entry.persona === persona);
  return (match ?? themeRegistry[0]!).id;
}

/** True when `id` is a registered theme. */
export function isThemeId(id: string): boolean {
  return REGISTRY_BY_ID.has(id);
}

/** Look up a registered theme by id (undefined when unknown). */
export function getRegisteredTheme(id: string): RegisteredTheme | undefined {
  return REGISTRY_BY_ID.get(id);
}
